import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Stack,
  Box,
  CircularProgress,
  Typography,
  Checkbox,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Check, Droplets, FlaskConical } from "lucide-react";
import axios from "axios";
import { PaymentIcon } from "react-svg-credit-card-payment-icons";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import {
  pool,
  API_URL,
  BIOAGE,
  VITAMINS,
  OMEGA,
  HORM_F,
  HORM_M,
  PROSTATE,
  THYROID,
  ATHERO,
  BONES,
  OVARIAN,
  GUT5,
  COLON,
} from "../../../constants";
import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import OnbSteps from "../../../components/Steps/OnbSteps";
import ButtonForm from "../../../components/Button/ButtonForm";
import FormAlert from "../../../components/Alert/FormAlert";
import Apple_Pay from "../../../assets/Apple_Pay.svg";
import Klarna from "../../../assets/icons/Klarna.png";

function cardSx(theme, accentColor) {
  return {
    border: '1px solid',
    borderRadius: 4,
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

function NumberedBoxHeader({ number, heading }) {
  const theme = useTheme();
  const numberColor = theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100;
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 28, height: 28,
          borderRadius: '50%',
          border: `2px solid ${numberColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Typography variant="number4" sx={{ color: numberColor, fontWeight: 400, fontSize: '0.75rem', lineHeight: 1 }}>
          {number}
        </Typography>
      </Box>
      <Typography variant="subtitle1" fontWeight={400}>{heading}</Typography>
    </Stack>
  );
}

export default function AddonSelection() {
  const { state } = useAuth();
  const { t, changeLanguage } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [selectedAddOns, setSelectedAddOns] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [addonProducts, setAddonProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);

  const [skipLoading, setSkipLoading] = useState(false);
  const [skipError, setSkipError] = useState(null);

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  useEffect(() => {
    async function fetchAddonProducts() {
      try {
        const response = await axios.get(`${API_URL}/api/addon-products/`);
        setAddonProducts(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Failed to fetch addon products:", err);
        setProductsError(t("addonsel_products_err_load"));
      } finally {
        setProductsLoading(false);
      }
    }
    fetchAddonProducts();
  }, []);

  // ------------------ Add-on definitions — sku/name-key/image/description-key/markers-key are
  // local (the API has no localized name, image, or marker text); price and availability come
  // from /api/addon-products/. `rawMarkers: true` mirrors AddOnInfoCards/BasePackageCard.jsx —
  // those two marker strings are already separator-formatted, the rest are comma-separated.
  const ADDON_BLOOD_META = [
    { sku: "age", nameKey: "add_bio", descKey: "card_bio_age_desc", markersKey: "card_bio_age_markers", rawMarkers: true, image: BIOAGE },
    { sku: "vitamins", nameKey: "add_vit", descKey: "card_vitamins_desc", markersKey: "card_vitamins_markers", image: VITAMINS },
    { sku: "omega", nameKey: "add_omega", descKey: "card_omega_desc", markersKey: "card_omega_markers", image: OMEGA },
    { sku: "horm_female", nameKey: "add_hormf", descKey: "card_female_hormone_desc", markersKey: "card_female_hormone_markers", image: HORM_F },
    { sku: "horm_male", nameKey: "add_hormm", descKey: "card_male_hormone_desc", markersKey: "card_male_hormone_markers", image: HORM_M },
    { sku: "psa", nameKey: "add_psa", descKey: "card_prostate_desc", markersKey: "card_prostate_markers", image: PROSTATE },
    { sku: "thyroid", nameKey: "add_thyr", descKey: "card_thyroid_desc", markersKey: "card_thyroid_markers", image: THYROID },
    { sku: "atherosclerosis", nameKey: "add_ath", descKey: "card_athero_desc", markersKey: "card_athero_markers", image: ATHERO },
    { sku: "bones", nameKey: "add_bones", descKey: "card_bones_desc", markersKey: "card_bones_markers", image: BONES },
    { sku: "fertility", nameKey: "add_ov", descKey: "card_ovarian_desc", markersKey: "card_ovarian_markers", image: OVARIAN },
  ];

  const ADDON_STOOL_META = [
    { sku: "gutmicrobiome", nameKey: "add_gut", descKey: "card_gut_desc", markersKey: "card_gut_markers", rawMarkers: true, image: GUT5 },
    { sku: "ifob", nameKey: "add_fob", descKey: "card_colon_desc", markersKey: "card_colon_markers", image: COLON },
  ];

  const productBySku = Object.fromEntries(
    addonProducts.filter((p) => p.active).map((p) => [p.sku, p])
  );

  const formatMarkers = (raw, isRaw) => {
    if (!raw) return "";
    return isRaw ? raw : raw.split(",").map((m) => m.trim()).join(" · ");
  };

  const buildAddonList = (meta) =>
    meta
      .filter((m) => productBySku[m.sku])
      .map((m) => ({
        id: m.sku,
        name: t(m.nameKey),
        description: t(m.descKey),
        markers: formatMarkers(t(m.markersKey), m.rawMarkers),
        image: m.image,
        price: parseFloat(productBySku[m.sku].price_eur),
      }));

  const addOnBlood = buildAddonList(ADDON_BLOOD_META);
  const addOnStool = buildAddonList(ADDON_STOOL_META);

  const allAddOns = [...addOnBlood, ...addOnStool];

  const toggleAddOn = (id) => {
    setSelectedAddOns((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  // ------------------ Gender-aware Select All ------------------
  const getSelectableAddOnIdsByGender = (gender) =>
    allAddOns
      .filter((a) => {
        if (gender === "M") return !["horm_female", "fertility"].includes(a.id);
        if (gender === "F") return !["horm_male", "psa"].includes(a.id);
        return true;
      })
      .map((a) => a.id);

  const handleSelectAllByGender = (gender) => {
    const ids = getSelectableAddOnIdsByGender(gender);
    const allSelected = ids.every((id) => selectedAddOns.includes(id));
    if (allSelected) {
      setSelectedAddOns((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedAddOns((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const menIds = getSelectableAddOnIdsByGender("M");
  const womenIds = getSelectableAddOnIdsByGender("F");
  const allMenSelected = menIds.length > 0 && menIds.every((id) => selectedAddOns.includes(id));
  const allWomenSelected = womenIds.length > 0 && womenIds.every((id) => selectedAddOns.includes(id));

  // ------------------ Total ------------------
  const addOnTotal = allAddOns
    .filter((a) => selectedAddOns.includes(a.id))
    .reduce((sum, a) => sum + a.price, 0);

  // ------------------ Add-on section renderer ------------------
  const AddOnSection = ({ title, icon: Icon, iconColor, items }) => (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{
          display: 'inline-flex',
          border: '1px solid',
          borderColor: theme.palette.greydark.C200,
          borderRadius: 4,
          px: 1.5,
          py: 0.5,
        }}>
          {Icon && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 26, height: 26, borderRadius: '50%',
              bgcolor: iconColor + '1a',
              flexShrink: 0,
            }}>
              <Icon size={14} color={iconColor} />
            </Box>
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Stack>
      </Box>

      <Box>
        {items.map((a, index) => (
          <Box
            key={a.id}
            sx={{
              display: 'flex',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              alignItems: 'flex-start',
              gap: 1.5,
              bgcolor: index % 2 === 1 ? 'rgba(0,0,0,0.008)' : 'rgba(0,0,0,0.035)',
              px: 2,
              py: 1.5,
            }}
          >
            <Checkbox
              checked={selectedAddOns.includes(a.id)}
              onChange={() => toggleAddOn(a.id)}
              sx={{
                mt: 0.5,
                color: "greydark.C500",
                "&.Mui-checked": { color: "greydark.C500" },
                p: { xs: 0.5, sm: 1 },
              }}
            />

            <Box sx={{ flexShrink: 0, width: 96 }}>
              <Box sx={{ position: 'relative', width: 96, height: 72, borderRadius: 2, overflow: 'hidden' }}>
                <Box
                  component="img"
                  src={a.image}
                  alt={a.name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 4,
                    right: 4,
                    bgcolor: 'rgba(15,15,15,0.6)',
                    borderRadius: '99px',
                    px: 0.75,
                    py: 0.15,
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
                    € {a.price},-
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ mb: 0.25 }}>
                {a.name}
              </Typography>
              <Typography variant="subtitle2" fontWeight={200} sx={{ lineHeight: 1.3 }}>
                {a.description}
              </Typography>
              {a.markers && (
                <Typography variant="subtitle2" fontWeight={200} sx={{ display: 'block', mt: 0.5, lineHeight: 1.3 }}>
                  {a.markers}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );

  // ------------------ Checkout ------------------
  const STATUS_FALLBACK_KEYS = {
    400: "addonsel_err_400",
    403: "addonsel_err_403",
    404: "addonsel_err_404",
  };

  const handleCheckout = async () => {
    setErrorMessage(null);
    setLoading(true);
    try {
      const payload = {
        addons: selectedAddOns.map((sku) => ({ sku, quantity: 1 })),
      };
      const response = await axios.post(`${API_URL}/api/onboarding/addon/checkout/`, payload);
      const { checkout_url } = response.data;
      if (checkout_url) {
        window.location.href = checkout_url;
        return;
      }
      setErrorMessage(t("val_server_error"));
    } catch (err) {
      const backendError = err.response?.data?.error;
      const fallbackKey = STATUS_FALLBACK_KEYS[err.response?.status];
      setErrorMessage(backendError || (fallbackKey ? t(fallbackKey) : t("val_server_error")));
    } finally {
      setLoading(false);
    }
  };

  // ------------------ Skip straight to the questionnaire ------------------
  const handleSkipToQuestionnaire = async () => {
    setSkipError(null);
    setSkipLoading(true);
    try {
      await axios.patch(`${API_URL}/api/onboarding/status/`, {
        onboarding_status: 4,
      });
      axios.post(`${API_URL}/api/lab-visits/send_email/`).catch((err) => {
        console.error("Failed to send lab visit confirmation email:", err);
      });
      navigate("/NewQuestionnaire");
    } catch (err) {
      console.error("Failed to advance onboarding status:", err);
      setSkipError(t("val_server_error"));
    } finally {
      setSkipLoading(false);
    }
  };

  const accentLeft = theme.palette.tertiary.C300;
  const accentRight = theme.palette.quartiary.C300;
  const selectedItems = allAddOns.filter((a) => selectedAddOns.includes(a.id));

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext={t("onbsub_brandtext")}
      content={
        <>
          <Grid container sx={{ mb: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <OnbSteps stepNumber={1} />
            </Grid>
          </Grid>

          <Grid container spacing={4} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, mx: 'auto' }}>
            <Grid size={12}>
              <Box sx={{ px: 3 }}>

                {/* Skip straight to the questionnaire — no add-ons this time */}
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    {t("addonsel_intro_text")}
                  </Typography>

                  {skipLoading ? (
                    <Grid container sx={{ justifyContent: 'center', my: 1 }}>
                      <CircularProgress size={20} />
                    </Grid>
                  ) : (
                    <ButtonForm
                      name={t("addonsel_skip_button")}
                      onClick={handleSkipToQuestionnaire}
                    />
                  )}

                  {skipError && <FormAlert severity="error" message={skipError} sx={{ mt: 1 }} />}
                </Box>

                {/* Box 1 — select add-ons */}
                <Accordion
                  disableGutters
                  elevation={0}
                  defaultExpanded
                  sx={{
                    ...cardSx(theme, accentLeft),
                    // MUI's Accordion :first-of-type/:last-of-type defaults outrank a plain
                    // sx borderRadius (same fix as FirstLabvisit.jsx) — without !important the
                    // top-left corner renders square instead of matching Box 2's radius.
                    borderRadius: '16px !important',
                    mb: 3,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 2.5,
                      py: 1,
                      '& .MuiAccordionSummary-content': { my: 1 },
                    }}
                  >
                    <NumberedBoxHeader number={1} heading={t("addonsel_step1_heading")} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
                      <Chip
                        label={allMenSelected ? t("deselect_all_men") : t("select_all_men")}
                        onClick={() => handleSelectAllByGender("M")}
                        size="small"
                        variant={allMenSelected ? "filled" : "outlined"}
                        icon={allMenSelected ? <Check size={13} /> : undefined}
                        sx={{ fontSize: '0.72rem', fontWeight: 500 }}
                      />
                      <Chip
                        label={allWomenSelected ? t("deselect_all_women") : t("select_all_women")}
                        onClick={() => handleSelectAllByGender("F")}
                        size="small"
                        variant={allWomenSelected ? "filled" : "outlined"}
                        icon={allWomenSelected ? <Check size={13} /> : undefined}
                        sx={{ fontSize: '0.72rem', fontWeight: 500 }}
                      />
                    </Stack>

                    {productsLoading ? (
                      <Grid container sx={{ justifyContent: 'center', my: 2 }}>
                        <CircularProgress size={24} />
                      </Grid>
                    ) : productsError ? (
                      <FormAlert severity="error" message={productsError} />
                    ) : (
                      <>
                        <AddOnSection title={t("onb_subscr13")} icon={Droplets} iconColor="#e53935" items={addOnBlood} />
                        <AddOnSection title={t("onb_subscr14")} icon={FlaskConical} iconColor="#8d6e63" items={addOnStool} />
                      </>
                    )}
                  </AccordionDetails>
                </Accordion>

                {/* Box 2 — review & pay */}
                <Box sx={{ ...cardSx(theme, accentRight), p: 3 }}>
                  <NumberedBoxHeader number={2} heading={t("addonsel_step2_heading")} />

                  {selectedItems.length > 0 && <Divider sx={{ mb: 2 }} />}

                  {selectedItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t("addonsel_empty")}
                    </Typography>
                  ) : (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {selectedItems.map((a) => (
                        <Stack key={a.id} direction="row" justifyContent="space-between">
                          <Typography variant="body2">{a.name}</Typography>
                          <Typography variant="body2" fontWeight={400}>€ {a.price},-</Typography>
                        </Stack>
                      ))}
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography fontWeight={600}>{t("onb_subscr15")}</Typography>
                        <Typography variant="h6" fontWeight={600}>€ {addOnTotal.toFixed(2)}</Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t("onbsub_note_vat")}
                      </Typography>
                    </Stack>
                  )}

                  {loading ? (
                    <Grid container sx={{ justifyContent: 'center', my: 2 }}>
                      <CircularProgress size={24} />
                    </Grid>
                  ) : (
                    <ButtonForm
                      name={t("onb_btn_secure_payment")}
                      onClick={handleCheckout}
                      disabled={loading || selectedItems.length === 0}
                    />
                  )}

                  <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                    <PaymentIcon type="visa" format="flatRounded" width={36} />
                    <PaymentIcon type="mastercard" format="flatRounded" width={36} />
                    <PaymentIcon type="paypal" format="flatRounded" width={36} />
                    <Box mb={1}>
                      <div><img src={Apple_Pay} alt="ApplePay" width={36} height={27} /></div>
                    </Box>
                    <Box mb={1}>
                      <div><img src={Klarna} alt="Klarna" height={22} /></div>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="caption" color="text.secondary">
                    {t("addonsel_note_next_visit")}
                  </Typography>

                  {selectedAddOns.includes("gutmicrobiome") && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t("addonsel_note_gutmicrobiome")}
                    </Typography>
                  )}

                  {selectedAddOns.includes("ifob") && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {t("addonsel_note_ifob")}
                    </Typography>
                  )}

                  {errorMessage && <FormAlert severity="error" message={errorMessage} sx={{ mt: 2 }} />}
                </Box>

              </Box>
            </Grid>
          </Grid>
        </>
      }
    />
  );
}
